import { parseGeoData } from "senf-shared";
import type { Project } from "senf-shared/types";
import {
  CLEAR_DATA_ERROR,
  CLEAR_DATA_SUCCESS,
  DELETE_COMMENT,
  DELETE_IDEA,
  EDIT_IDEA,
  LIKE_IDEA,
  LOADING_DATA,
  POST_IDEA,
  RESET_PROFILE_PAGE,
  SET_COMMENTS,
  SET_COOKIES,
  SET_DATA_ERROR,
  SET_DATA_SUCCESS,
  SET_IDEA,
  SET_INITIAL_MAP_BOUNDS,
  SET_MAP_BOUNDS,
  SET_PROFILE_PAGE,
  SET_PROJECT,
  STOP_LOADING_DATA,
  SUBMIT_COMMENT,
  UNLIKE_IDEA,
} from "../types";

export interface ClientState {
  data: {
    dataError: string;
    dataSuccess: string;
    idea: any;
    project: Project;
    loading: boolean;
    profilePage: {
      profilePageData: any;
    };
    consent_settings: string;
    mapLoaded: boolean;
    mapViewport: any;
    initialMapBounds: any;
    mapBounds: any;
  };
}
const initialState = {
  dataError: "",
  dataSuccess: "",
  idea: null,
  project: null,
  loading: true,
  profilePage: {
    profilePageData: {},
  },
  consent_settings: "",
  mapLoaded: false,
  mapViewport: null,
  initialMapBounds: null,
  mapBounds: null,
};

export default function (state = initialState, action) {
  switch (action.type) {
    case SET_DATA_ERROR:
      return { ...state, dataError: action.payload };
    case CLEAR_DATA_ERROR:
      return { ...state, dataError: "" };
    case SET_DATA_SUCCESS:
      return { ...state, dataSuccess: action.payload };
    case CLEAR_DATA_SUCCESS:
      return { ...state, dataSuccess: "" };
    case STOP_LOADING_DATA:
      return {
        ...state,
        loading: false,
      };
    case LOADING_DATA:
      return {
        ...state,
        loading: true,
      };

    case SET_IDEA:
      return {
        ...state,
        idea: action.payload,
      };

    case SET_PROFILE_PAGE:
      return {
        ...state,
        profilePage: {
          ...state.profilePage,
          profilePageData: {
            ...state.profilePage.profilePageData,
            ...action.payload,
          },
        },
      };
    case RESET_PROFILE_PAGE:
      return {
        ...state,
        profilePage: {
          ...state.profilePage,
          profilePageData: {},
        },
      };

    case LIKE_IDEA: {
      const incrementLikeCount = (idea) =>
        idea.ideaId === action.payload.ideaId
          ? { ...idea, likeCount: idea.likeCount + 1 }
          : idea;

      const updatedIdeaMaps = state.project.ideaMaps.map((ideaMap) => ({
        ...ideaMap,
        ideas: ideaMap.ideas.map(incrementLikeCount),
      }));

      return {
        ...state,
        idea:
          state.idea?.ideaId === action.payload.ideaId
            ? incrementLikeCount(state.idea)
            : state.idea,
        project: {
          ...state.project,
          ideaMaps: updatedIdeaMaps,
        },
        profilePage: {
          ...state.profilePage,
          profilePageData: {
            ...state.profilePage.profilePageData,
            ideas:
              state.profilePage.profilePageData?.ideas?.map(incrementLikeCount),
          },
        },
      };
    }

    case UNLIKE_IDEA: {
      const decrementLikeCount = (idea) =>
        idea.ideaId === action.payload.ideaId
          ? { ...idea, likeCount: idea.likeCount - 1 }
          : idea;

      const updatedIdeaMaps = state.project.ideaMaps.map((ideaMap) => ({
        ...ideaMap,
        ideas: ideaMap.ideas.map(decrementLikeCount),
      }));

      return {
        ...state,
        idea:
          state.idea?.ideaId === action.payload.ideaId
            ? decrementLikeCount(state.idea)
            : state.idea,
        project: {
          ...state.project,
          ideaMaps: updatedIdeaMaps,
        },
        profilePage: {
          ...state.profilePage,
          profilePageData: {
            ...state.profilePage.profilePageData,
            ideas:
              state.profilePage.profilePageData?.ideas?.map(decrementLikeCount),
          },
        },
      };
    }

    case DELETE_IDEA: {
      const updatedIdeaMaps = state.project.ideaMaps.map((ideaMap) => ({
        ...ideaMap,
        ideas: ideaMap.ideas.filter((idea) => idea.ideaId !== action.payload),
      }));

      return {
        ...state,
        project: {
          ...state.project,
          ideaMaps: updatedIdeaMaps,
        },
      };
    }

    case SET_COMMENTS:
      return {
        ...state,
        loading: false,
        idea: {
          ...state.idea,
          comments: action.payload,
        },
      };

    case POST_IDEA: {
      const updatedIdeaMaps = state.project.ideaMaps.map((ideaMap) => {
        if (ideaMap && ideaMap?.ideaMapId === action.payload.ideaMapId) {
          return {
            ...ideaMap,
            ideas: [
              ...ideaMap.ideas,
              {
                ...action.payload,
                geoData:
                  action.payload.geoDataType === "location" ||
                  action.payload.geoDataType === "noLocation"
                    ? null
                    : {
                        ...parseGeoData(action.payload.geoData),
                        features: parseGeoData(
                          action.payload.geoData,
                        )?.features.map((feature) => ({
                          ...feature,
                          properties: {
                            ...feature.properties,
                            ideaId: action.payload.ideaId,
                            title: action.payload.title,
                            color:
                              ideaMap?.customTopicsObject?.[
                                action.payload.topic
                              ]?.color,
                          },
                        })),
                      },
              },
            ],
          };
        }
        return ideaMap;
      });

      return {
        ...state,
        project: {
          ...state.project,
          ideaMaps: updatedIdeaMaps,
        },
        loading: false,
      };
    }

    case EDIT_IDEA: {
      const updateIdeasList = (ideas, payload) => {
        return ideas.map((idea) =>
          idea.ideaId === payload.ideaId ? { ...idea, ...payload } : idea,
        );
      };

      const updatedIdeaMaps = state.project.ideaMaps.map((ideaMap) => ({
        ...ideaMap,
        ideas: updateIdeasList(ideaMap.ideas, action.payload),
      }));

      return {
        ...state,
        idea:
          state.idea?.ideaId === action.payload.ideaId
            ? { ...state.idea, ...action.payload }
            : state.idea,
        project: {
          ...state.project,
          ideaMaps: updatedIdeaMaps,
        },
        profilePage: {
          ...state.profilePage,
          profilePageData: {
            ...state.profilePage.profilePageData,
            ideas: state.profilePage.profilePageData?.ideas
              ? updateIdeasList(
                  state.profilePage.profilePageData.ideas,
                  action.payload,
                )
              : [],
          },
        },
      };
    }

    case SUBMIT_COMMENT: {
      const updateIdeaWithComment = (idea, payload) => {
        if (idea.ideaId === payload.ideaId) {
          return {
            ...idea,
            commentCount: idea.commentCount + 1,
            comments: idea.comments ? [payload, ...idea.comments] : [payload],
          };
        }
        return idea;
      };

      const updatedIdeaMaps = state.project.ideaMaps.map((ideaMap) => ({
        ...ideaMap,
        ideas: ideaMap.ideas.map((idea) =>
          updateIdeaWithComment(idea, action.payload),
        ),
      }));

      // Assuming you want to add comments array in profilePageData.ideas
      const updatedProfilePageIdeas = state.profilePage.profilePageData?.ideas
        ? state.profilePage.profilePageData.ideas.map((idea) =>
            updateIdeaWithComment(idea, action.payload),
          )
        : [];

      return {
        ...state,
        idea:
          state.idea?.ideaId === action.payload.ideaId
            ? updateIdeaWithComment(state.idea, action.payload)
            : state.idea,
        project: {
          ...state.project,
          ideaMaps: updatedIdeaMaps,
        },
        profilePage: {
          ...state.profilePage,
          profilePageData: {
            ...state.profilePage.profilePageData,
            ideas: updatedProfilePageIdeas,
          },
        },
      };
    }

    case DELETE_COMMENT:
      const listComments = state.idea.comments.filter(
        (comment) => comment.commentId !== action.payload,
      );
      const filteredIdeaComments = state.idea.comments.filter(
        (comment) => comment.commentId !== action.payload,
      );

      return {
        ...state,
        idea: {
          ...state.idea,
          comments: listComments,
          commentCount: state.idea.commentCount - 1,
        },
        project: state.project?.ideas
          ? {
              ...state.project,
              ideas: [
                ...state.project.ideas.map((idea) =>
                  idea.ideaId === state.idea.ideaId
                    ? {
                        ...idea,
                        commentCount: idea.commentCount - 1,
                      }
                    : idea,
                ),
              ],
            }
          : state.project,

        profilePage: state.profilePage.profilePageData.ideas
          ? {
              ...state.profilePage,
              profilePageData: {
                ...state.profilePage.profilePageData,
                ideas: [
                  ...state.profilePage.profilePageData.ideas.map((idea) =>
                    idea.ideaId === state.idea.ideaId
                      ? {
                          ...idea,
                          /*      comments: filteredIdeaComments,

            right now profilePageData.ideas does not have array of comments,
            maybe it has to be added ? */
                          commentCount: idea.commentCount - 1,
                        }
                      : idea,
                  ),
                ],
              },
            }
          : state.profilePage,
      };

    case SET_PROJECT:
      return {
        ...state,
        project: action.payload,
      };

    case SET_COOKIES:
      return {
        ...state,
        consent_settings: action.payload,
      };

    case SET_MAP_BOUNDS:
      return {
        ...state,
        mapBounds: action.payload,
      };

    case SET_INITIAL_MAP_BOUNDS:
      return {
        ...state,
        initialMapBounds: action.payload,
      };

    default:
      return state;
  }
}
